import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import "./ViewExamRecords.css";
import Sidebar from "./Sidebar";
import {
  FaPaperPlane,
  FaPrint,
  FaEdit,
  FaSave,
  FaTimes,
  FaCheck,
  FaCalculator,
  FaSearch,
  FaArrowUp,
  FaArrowDown,
  FaClipboardCheck,
  FaBook,
  FaCalendarAlt,
  FaUserGraduate,
  FaSpinner,
  FaExclamationTriangle,
  FaDatabase,
  FaCheckCircle,
  FaExclamationCircle,
  FaIdCard,
  FaFilter,
  FaPen,
  FaInfoCircle,
  FaSync,
  FaHistory,
  FaChartLine,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaEye,
  FaEyeSlash,
  FaDownload,
  FaUpload,
  FaTrash,
  FaCog,
} from "react-icons/fa";

// PHP API Base URL - Adjust this to match your setup
const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php";

const ViewExamRecords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { examId } = useParams();
  const inputRefs = useRef([]);
  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);

  // State management
  const [examDetails, setExamDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [originalStudents, setOriginalStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
  const [stats, setStats] = useState({
    totalStudents: 0,
    submittedStudents: 0,
    averageMarks: 0,
    highestMarks: 0,
    lowestMarks: 0,
    completionRate: 0,
    avgPercentage: 0,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [examHistory, setExamHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [tableScroll, setTableScroll] = useState({ x: 0, y: 0 });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [visibleColumns, setVisibleColumns] = useState({
    studentId: true,
    studentName: true,
    class: true,
    classSection: true,
    subject: true,
    obtainedMarks: true,
    percentage: true,
    examType: true,
    examNo: false,
    totalMarks: true,
    examDate: false,
    examDay: false,
  });

  // Calculate statistics
  const calculateStatistics = useCallback((studentList) => {
    if (!studentList || studentList.length === 0) {
      setStats({
        totalStudents: 0,
        submittedStudents: 0,
        averageMarks: 0,
        highestMarks: 0,
        lowestMarks: 0,
        completionRate: 0,
        avgPercentage: 0,
      });
      return;
    }

    const totalStudents = studentList.length;
    const submittedStudents = studentList.filter(
      (s) => parseFloat(s.obtained_marks) > 0
    ).length;
    const totalMarks = studentList.reduce(
      (sum, s) => sum + parseFloat(s.obtained_marks || 0),
      0
    );
    const averageMarks = totalStudents > 0 ? totalMarks / totalStudents : 0;
    const highestMarks = Math.max(
      ...studentList.map((s) => parseFloat(s.obtained_marks || 0))
    );
    const lowestMarks =
      Math.min(
        ...studentList
          .filter((s) => parseFloat(s.obtained_marks || 0) > 0)
          .map((s) => parseFloat(s.obtained_marks || 0))
      ) || 0;
    const totalPercentage = studentList.reduce(
      (sum, s) => sum + parseFloat(s.percentage || 0),
      0
    );
    const avgPercentage =
      totalStudents > 0 ? totalPercentage / totalStudents : 0;
    const completionRate =
      totalStudents > 0 ? (submittedStudents / totalStudents) * 100 : 0;

    setStats({
      totalStudents,
      submittedStudents,
      averageMarks: averageMarks.toFixed(2),
      highestMarks: highestMarks.toFixed(2),
      lowestMarks: lowestMarks.toFixed(2),
      avgPercentage: avgPercentage.toFixed(2),
      completionRate: completionRate.toFixed(1),
    });
  }, []);

  // Fetch exam details and records by exam ID
  const fetchExamRecords = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching exam records for ID:", examId);

      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "get-exam-by-id",
          exam_id: examId,
        },
      });

      console.log("Exam data response:", response.data);

      if (response.data.success) {
        const examData = response.data.data;

        if (examData && examData.length > 0) {
          const firstRecord = examData[0];

          // Set exam details from the actual database fields
          setExamDetails({
            exam_id: examId,
            exam_type: firstRecord.exam_type,
            subject: firstRecord.subject,
            class: firstRecord.class,
            class_section: firstRecord.class_section,
            exam_date: firstRecord.exam_date,
            exam_day: firstRecord.exam_day,
            total_marks: parseFloat(firstRecord.total_marks) || 100,
            exam_no: firstRecord.exam_no || null,
          });

          // Transform student data according to actual database fields
          const studentData = examData.map((record, index) => {
            const obtainedMarks = parseFloat(record.obtained_marks) || 0;
            const totalMarks = parseFloat(record.total_marks) || 100;
            const percentage = parseFloat(record.percentage) || 0;

            return {
              id: record.id || record.student_id,
              student_id: record.student_id,
              student_name: record.student_name,
              class: record.class,
              class_section: record.class_section,
              subject: record.subject,
              obtained_marks: obtainedMarks,
              percentage: percentage,
              exam_type: record.exam_type,
              exam_no: record.exam_no,
              total_marks: totalMarks,
              exam_date: record.exam_date,
              exam_day: record.exam_day,
              grade: calculateGrade(percentage),
              status: obtainedMarks > 0 ? "submitted" : "pending",
              index: index,
            };
          });

          setStudents(studentData);
          setFilteredStudents(studentData);
          setOriginalStudents(JSON.parse(JSON.stringify(studentData))); // Deep copy
          calculateStatistics(studentData);
        } else {
          Swal.fire({
            title: "No Records Found",
            text: "No exam records found for the provided exam ID.",
            icon: "warning",
            confirmButtonText: "OK",
          }).then(() => {
            navigate("/exam-records");
          });
        }
      } else {
        throw new Error(response.data.error || "Failed to fetch exam records");
      }
    } catch (error) {
      console.error("Error fetching exam records:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "Failed to load exam records. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/exam-records");
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch exam history for the same class, subject, and section
  const fetchExamHistory = async () => {
    if (!examDetails) return;

    try {
      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "get-exam-history",
          class: examDetails.class,
          subject: examDetails.subject,
          section: examDetails.class_section,
        },
      });

      if (response.data.success) {
        setExamHistory(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching exam history:", error);
    }
  };

  // Calculate grade based on percentage
  const calculateGrade = (percentage) => {
    if (percentage >= 90) return "A+";
    if (percentage >= 80) return "A";
    if (percentage >= 70) return "B";
    if (percentage >= 60) return "C";
    if (percentage >= 50) return "D";
    return "F";
  };

  // Handle marks update
  const handleMarksUpdate = (e, studentId, index) => {
    const value = parseFloat(e.target.value) || 0;
    const totalMarks = examDetails?.total_marks || 100;

    if (value > totalMarks) {
      Swal.fire({
        title: "Invalid Input",
        text: `Obtained marks cannot exceed ${totalMarks}.`,
        icon: "warning",
        confirmButtonText: "OK",
        timer: 1500,
      });

      if (inputRefs.current[index]) {
        inputRefs.current[index].focus();
        inputRefs.current[index].select();
      }
      return;
    }

    const updatedStudents = students.map((student) =>
      student.id === studentId
        ? {
            ...student,
            obtained_marks: value,
            percentage:
              totalMarks > 0 ? ((value / totalMarks) * 100).toFixed(2) : 0,
            grade: calculateGrade((value / totalMarks) * 100),
            status: value > 0 ? "submitted" : "pending",
          }
        : student
    );

    setStudents(updatedStudents);
    setFilteredStudents(updatedStudents);

    // Check for changes
    const originalStudent = originalStudents.find((s) => s.id === studentId);
    if (originalStudent?.obtained_marks !== value) {
      setHasChanges(true);
    }

    // Recalculate statistics
    calculateStatistics(updatedStudents);
  };

  // Handle bulk marks update
  const handleBulkMarksUpdate = (marks) => {
    const totalMarks = examDetails?.total_marks || 100;
    const updatedStudents = students.map((student) => ({
      ...student,
      obtained_marks: marks,
      percentage: totalMarks > 0 ? ((marks / totalMarks) * 100).toFixed(2) : 0,
      grade: calculateGrade((marks / totalMarks) * 100),
      status: marks > 0 ? "submitted" : "pending",
    }));

    setStudents(updatedStudents);
    setFilteredStudents(updatedStudents);
    setHasChanges(true);
    calculateStatistics(updatedStudents);
  };

  // Update exam records
  const handleUpdateRecords = async () => {
    if (!hasChanges) {
      Swal.fire({
        title: "No Changes",
        text: "No changes detected to update.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }

    const incompleteStudents = students.filter(
      (s) => parseFloat(s.obtained_marks) === 0
    );
    if (incompleteStudents.length > 0) {
      const result = await Swal.fire({
        title: "Incomplete Data",
        html: `${incompleteStudents.length} student(s) have 0 marks. Continue anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Update Anyway",
        cancelButtonText: "Review",
        confirmButtonColor: "#00b09b",
        cancelButtonColor: "#6c757d",
      });
      if (!result.isConfirmed) return;
    }

    Swal.fire({
      title: "Update Exam Records",
      html: `
        <div style="text-align: left;">
          <p>This will update all exam marks in the database.</p>
          <p><strong>Exam Details:</strong></p>
          <ul style="margin-left: 20px;">
            <li>Exam ID: ${examId}</li>
            <li>Exam Type: ${examDetails?.exam_type}</li>
            <li>Subject: ${examDetails?.subject}</li>
            <li>Class: ${examDetails?.class}</li>
            <li>Section: ${examDetails?.class_section}</li>
            <li>Total Students: ${students.length}</li>
            <li>Total Marks: ${examDetails?.total_marks}</li>
            ${
              examDetails?.exam_no
                ? `<li>Exam No: ${examDetails.exam_no}</li>`
                : ""
            }
          </ul>
          <p>Continue?</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6a11cb",
      cancelButtonColor: "#6c757d",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsUpdating(true);

          // Prepare payload according to PHP backend structure
          const payload = {
            examId: examId,
            testResults: students.map((student) => ({
              studentId: student.student_id,
              obtainedMarks: parseFloat(student.obtained_marks),
            })),
          };

          console.log("Updating with payload:", payload);

          // Send as form data with action parameter
          const formData = new FormData();
          formData.append("action", "update-exam-marks");
          formData.append("data", JSON.stringify(payload));

          const response = await axios.post(PHP_API_BASE, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          console.log("Update response:", response.data);

          if (response.data.success) {
            Swal.fire({
              title: "Success!",
              html: `
                <div style="text-align: left;">
                  <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
                    <p style="color: #48bb78; font-weight: 600;">
                      ✓ ${response.data.message}
                    </p>
                  </div>
                  <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
                    <p style="margin: 0 0 8px 0; font-weight: 600;">Update Summary:</p>
                    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                      <div style="color: #38a169;">
                        <span style="font-weight: 600;">✓ Students Updated:</span> ${
                          response.data.stats?.successful || students.length
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🆔 Exam ID:</span> ${examId}
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📅 Date:</span> ${
                          examDetails?.exam_date
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📚 Subject:</span> ${
                          examDetails?.subject
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🏫 Class:</span> ${
                          examDetails?.class
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">👥 Section:</span> ${
                          examDetails?.class_section
                        }
                      </div>
                    </div>
                  </div>
                </div>
              `,
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: "#00b09b",
            });

            // Update original students
            setOriginalStudents(JSON.parse(JSON.stringify(students)));
            setHasChanges(false);

            // Refresh exam details
            fetchExamRecords();
          } else {
            throw new Error(response.data.error || "Update failed");
          }
        } catch (error) {
          console.error("Update error:", error);
          Swal.fire({
            title: "Update Failed",
            text:
              error.response?.data?.error || "Failed to update exam records",
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          setIsUpdating(false);
        }
      }
    });
  };

  // Reset changes
  const handleResetChanges = () => {
    Swal.fire({
      title: "Reset Changes",
      text: "Are you sure you want to discard all changes?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Reset",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        const resetStudents = JSON.parse(JSON.stringify(originalStudents));
        setStudents(resetStudents);
        setFilteredStudents(resetStudents);
        calculateStatistics(resetStudents);
        setHasChanges(false);

        Swal.fire({
          title: "Reset Complete",
          text: "All changes have been discarded.",
          icon: "success",
          timer: 1500,
        });
      }
    });
  };

  // Handle print/send data
  const handlePrintData = () => {
    if (students.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No student data to print!",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    navigate("/Print-Exam-Sheet-Sample", {
      state: {
        students,
        examDetails: {
          ...examDetails,
          className: examDetails?.class,
          examId: examId,
        },
      },
    });
  };

  // Handle table scroll
  const handleTableScroll = () => {
    if (tableContainerRef.current) {
      setTableScroll({
        x: tableContainerRef.current.scrollLeft,
        y: tableContainerRef.current.scrollTop,
      });
    }
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedStudents = [...filteredStudents].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Handle numeric values
      if (["obtained_marks", "percentage", "total_marks"].includes(key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredStudents(sortedStudents);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === "") {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(
        (student) =>
          student.student_name.toLowerCase().includes(value) ||
          student.student_id.toLowerCase().includes(value) ||
          (student.class && student.class.toLowerCase().includes(value)) ||
          (student.subject && student.subject.toLowerCase().includes(value))
      );
      setFilteredStudents(filtered);
    }
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // Get performance color
  const getPerformanceColor = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 90) return "#00b09b"; // Excellent - Green
    if (perc >= 80) return "#38a169"; // Very Good - Dark Green
    if (perc >= 70) return "#6a11cb"; // Good - Purple
    if (perc >= 60) return "#805ad5"; // Above Average - Light Purple
    if (perc >= 50) return "#f7971e"; // Average - Orange
    if (perc >= 40) return "#e53e3e"; // Below Average - Red
    return "#c53030"; // Poor - Dark Red
  };

  // Get performance label
  const getPerformanceLabel = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 90) return "Excellent";
    if (perc >= 80) return "Very Good";
    if (perc >= 70) return "Good";
    if (perc >= 60) return "Above Average";
    if (perc >= 50) return "Average";
    if (perc >= 40) return "Below Average";
    return "Needs Improvement";
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (
        ["ArrowUp", "ArrowDown", "Enter"].includes(e.key) &&
        document.activeElement.className === "view-exam-records-marks-input"
      ) {
        e.preventDefault();
        const currentIndex = Array.from(
          document.querySelectorAll(".view-exam-records-marks-input")
        ).indexOf(document.activeElement);

        if (
          e.key === "ArrowDown" &&
          currentIndex < inputRefs.current.length - 1
        ) {
          const nextInput = inputRefs.current[currentIndex + 1];
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
            setCurrentFocusIndex(currentIndex + 1);
          }
        } else if (e.key === "ArrowUp" && currentIndex > 0) {
          const prevInput = inputRefs.current[currentIndex - 1];
          if (prevInput) {
            prevInput.focus();
            prevInput.select();
            setCurrentFocusIndex(currentIndex - 1);
          }
        } else if (e.key === "Enter") {
          if (currentIndex < inputRefs.current.length - 1) {
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
              setCurrentFocusIndex(currentIndex + 1);
            }
          } else {
            inputRefs.current[0]?.focus();
            inputRefs.current[0]?.select();
            setCurrentFocusIndex(0);
          }
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // Fetch exam records on mount
  useEffect(() => {
    if (examId) {
      fetchExamRecords();
    }
  }, [examId]);

  // Fetch exam history when exam details are loaded
  useEffect(() => {
    if (examDetails) {
      fetchExamHistory();
    }
  }, [examDetails]);

  // Add scroll event listener
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
      return () =>
        tableContainer.removeEventListener("scroll", handleTableScroll);
    }
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get column count for colspan
  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter((v) => v).length;
  };

  if (isLoading) {
    return (
      <div className="view-exam-records-screen">
        <div className="view-exam-records-dashboard-wrapper">
          <Sidebar />
          <main className="view-exam-records-main-content">
            <div className="view-exam-records-loading-overlay">
              <div className="view-exam-records-loading-spinner">
                <FaSpinner className="view-exam-records-spin" size={32} />
                <p>Loading exam records...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="view-exam-records-screen">
      <div className="view-exam-records-dashboard-wrapper">
        <Sidebar />
        <main
          className="view-exam-records-main-content"
          style={{ marginLeft: "18%" }}
        >
          {/* Header Section */}
          <div className="view-exam-records-header">
            <div className="view-exam-records-header-content">
              <div className="view-exam-records-header-left">
                <h1 className="view-exam-records-main-title">
                  <FaCalculator className="view-exam-records-title-icon" />
                  Exam Records - {examId}
                </h1>
                <div className="view-exam-records-class-info">
                  <span className="view-exam-records-class-badge">
                    {examDetails?.class}
                  </span>
                  <span className="view-exam-records-exam-type">
                    {examDetails?.exam_type}
                  </span>
                  <span className="view-exam-records-exam-subject">
                    {examDetails?.subject}
                  </span>
                  <span className="view-exam-records-exam-section">
                    {examDetails?.class_section}
                  </span>
                  <span className="view-exam-records-exam-date">
                    <FaCalendarAlt /> {formatDate(examDetails?.exam_date)}
                  </span>
                  <span className="view-exam-records-keyboard-badge">
                    <FaArrowUp />/<FaArrowDown /> Arrow Keys • Enter = Next
                  </span>
                </div>
              </div>

              <div className="view-exam-records-header-actions">
                <button
                  className="view-exam-records-action-btn view-exam-records-history-btn"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <FaHistory />
                  {showHistory ? "Hide History" : "Show History"}
                </button>
                <button
                  className="view-exam-records-action-btn view-exam-records-column-btn"
                  onClick={() => {
                    Swal.fire({
                      title: "Column Visibility",
                      html: `
                        <div style="text-align: left;">
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="studentId" ${
                              visibleColumns.studentId ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="studentId" style="cursor: pointer;">
                              Student ID
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="studentName" ${
                              visibleColumns.studentName ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="studentName" style="cursor: pointer;">
                              Student Name
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="class" ${
                              visibleColumns.class ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="class" style="cursor: pointer;">
                              Class
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="classSection" ${
                              visibleColumns.classSection ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="classSection" style="cursor: pointer;">
                              Section
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="subject" ${
                              visibleColumns.subject ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="subject" style="cursor: pointer;">
                              Subject
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="obtainedMarks" ${
                              visibleColumns.obtainedMarks ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="obtainedMarks" style="cursor: pointer;">
                              Obtained Marks
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="percentage" ${
                              visibleColumns.percentage ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="percentage" style="cursor: pointer;">
                              Percentage
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="examType" ${
                              visibleColumns.examType ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="examType" style="cursor: pointer;">
                              Exam Type
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="examNo" ${
                              visibleColumns.examNo ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="examNo" style="cursor: pointer;">
                              Exam No
                            </label>
                          </div>
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <input type="checkbox" id="totalMarks" ${
                              visibleColumns.totalMarks ? "checked" : ""
                            } 
                              style="margin-right: 10px;">
                            <label for="totalMarks" style="cursor: pointer;">
                              Total Marks
                            </label>
                          </div>
                        </div>
                      `,
                      showCancelButton: true,
                      confirmButtonText: "Apply",
                      cancelButtonText: "Cancel",
                      didOpen: () => {
                        // Add event listeners to checkboxes
                        document
                          .querySelectorAll('input[type="checkbox"]')
                          .forEach((cb) => {
                            cb.addEventListener("change", (e) => {
                              // Update local state
                              const id = e.target.id;
                              const checked = e.target.checked;

                              setVisibleColumns((prev) => ({
                                ...prev,
                                [id]: checked,
                              }));
                            });
                          });
                      },
                    });
                  }}
                >
                  <FaEye />
                  Columns
                </button>
                <button
                  className="view-exam-records-action-btn view-exam-records-print-btn"
                  onClick={handlePrintData}
                  disabled={students.length === 0}
                >
                  <FaPrint />
                  Print Sheet
                </button>
                {hasChanges && (
                  <button
                    className="view-exam-records-action-btn view-exam-records-reset-btn"
                    onClick={handleResetChanges}
                  >
                    <FaSync />
                    Reset Changes
                  </button>
                )}
                <button
                  className="view-exam-records-action-btn view-exam-records-update-btn"
                  onClick={handleUpdateRecords}
                  disabled={isUpdating || !hasChanges || students.length === 0}
                >
                  {isUpdating ? (
                    <>
                      <FaSpinner className="view-exam-records-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Update Records
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="view-exam-records-stats-grid">
              <div className="view-exam-records-stat-card view-exam-records-stat-total">
                <div className="view-exam-records-stat-icon">
                  <FaUserGraduate />
                </div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.totalStudents}
                  </div>
                  <div className="view-exam-records-stat-label">
                    Total Students
                  </div>
                </div>
              </div>

              <div className="view-exam-records-stat-card view-exam-records-stat-submitted">
                <div className="view-exam-records-stat-icon">✓</div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.submittedStudents}
                  </div>
                  <div className="view-exam-records-stat-label">
                    Marks Entered
                  </div>
                </div>
              </div>

              <div className="view-exam-records-stat-card view-exam-records-stat-avg">
                <div className="view-exam-records-stat-icon">📊</div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.averageMarks}
                  </div>
                  <div className="view-exam-records-stat-label">
                    Average Marks
                  </div>
                </div>
              </div>

              <div className="view-exam-records-stat-card view-exam-records-stat-avg-perc">
                <div className="view-exam-records-stat-icon">%</div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.avgPercentage}%
                  </div>
                  <div className="view-exam-records-stat-label">Average %</div>
                </div>
              </div>

              <div className="view-exam-records-stat-card view-exam-records-stat-high">
                <div className="view-exam-records-stat-icon">🏆</div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.highestMarks}
                  </div>
                  <div className="view-exam-records-stat-label">
                    Highest Marks
                  </div>
                </div>
              </div>

              <div className="view-exam-records-stat-card view-exam-records-stat-completion">
                <div className="view-exam-records-stat-icon">
                  <FaClipboardCheck />
                </div>
                <div className="view-exam-records-stat-content">
                  <div className="view-exam-records-stat-value">
                    {stats.completionRate}%
                  </div>
                  <div className="view-exam-records-stat-label">Completion</div>
                </div>
              </div>
            </div>

            {/* Exam History Panel */}
            {showHistory && examHistory.length > 0 && (
              <div className="view-exam-records-history-panel">
                <div className="view-exam-records-history-header">
                  <h3>
                    <FaHistory /> Exam History
                  </h3>
                  <small>
                    Previous exams for {examDetails?.class} -{" "}
                    {examDetails?.subject}
                  </small>
                </div>
                <div className="view-exam-records-history-grid">
                  {examHistory.slice(0, 5).map((exam, index) => (
                    <div key={index} className="view-exam-records-history-card">
                      <div className="view-exam-records-history-type">
                        {exam.exam_type}
                      </div>
                      <div className="view-exam-records-history-date">
                        {formatDate(exam.exam_date)}
                      </div>
                      <div className="view-exam-records-history-stats">
                        <span>Avg: {exam.avg_percentage || 0}%</span>
                        <span>Students: {exam.student_count || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Controls Section */}
            <div className="view-exam-records-controls-section">
              <div className="view-exam-records-search-box">
                <FaSearch className="view-exam-records-search-icon" />
                <input
                  type="text"
                  placeholder="Search students by name, ID, class, or subject..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="view-exam-records-search-input"
                  disabled={students.length === 0}
                />
                {searchTerm && (
                  <button
                    className="view-exam-records-clear-search"
                    onClick={() => {
                      setSearchTerm("");
                      setFilteredStudents(students);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="view-exam-records-bulk-actions">
                <span className="view-exam-records-bulk-label">
                  Quick Fill:
                </span>
                <input
                  type="number"
                  placeholder="Enter marks"
                  className="view-exam-records-bulk-input"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const marks = parseFloat(e.target.value);
                      if (!isNaN(marks)) {
                        handleBulkMarksUpdate(marks);
                        e.target.value = "";
                        if (inputRefs.current[0]) {
                          inputRefs.current[0].focus();
                          inputRefs.current[0].select();
                        }
                      }
                    }
                  }}
                  disabled={students.length === 0}
                />
                <span className="view-exam-records-keyboard-shortcut">
                  Enter to apply
                </span>
              </div>

              <div className="view-exam-records-action-buttons">
                <button
                  className="view-exam-records-action-btn view-exam-records-sort-btn"
                  onClick={() => handleSort("percentage")}
                >
                  <FaSort />
                  Sort by %
                </button>
                <button
                  className="view-exam-records-action-btn view-exam-records-print-btn"
                  onClick={handlePrintData}
                  disabled={students.length === 0}
                >
                  <FaPrint /> Print Preview
                </button>
                <button
                  className="view-exam-records-action-btn view-exam-records-update-btn"
                  onClick={handleUpdateRecords}
                  disabled={isUpdating || !hasChanges || students.length === 0}
                >
                  {isUpdating ? (
                    <>
                      <FaSpinner className="view-exam-records-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaSave /> Update Records
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Table Container - Scrollable Area */}
          <div
            className="view-exam-records-table-container"
            ref={tableContainerRef}
          >
            <div className="view-exam-records-table-wrapper">
              <table className="view-exam-records-table" ref={tableRef}>
                <thead className="view-exam-records-table-header">
                  <tr>
                    {visibleColumns.studentId && (
                      <th
                        className="view-exam-records-th view-exam-records-th-sticky view-exam-records-th-left"
                        onClick={() => handleSort("student_id")}
                        style={{ left: "0px", zIndex: 100 }}
                      >
                        Student ID {getSortIcon("student_id")}
                      </th>
                    )}
                    {visibleColumns.studentName && (
                      <th
                        className="view-exam-records-th view-exam-records-th-sticky view-exam-records-th-left"
                        onClick={() => handleSort("student_name")}
                        style={{
                          left: visibleColumns.studentId ? "120px" : "0px",
                          zIndex: 99,
                        }}
                      >
                        Student Name {getSortIcon("student_name")}
                      </th>
                    )}
                    {visibleColumns.class && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("class")}
                      >
                        Class {getSortIcon("class")}
                      </th>
                    )}
                    {visibleColumns.classSection && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("class_section")}
                      >
                        Section {getSortIcon("class_section")}
                      </th>
                    )}
                    {visibleColumns.subject && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("subject")}
                      >
                        Subject {getSortIcon("subject")}
                      </th>
                    )}
                    {visibleColumns.obtainedMarks && (
                      <th className="view-exam-records-th view-exam-records-th-obtained">
                        Obtained Marks
                      </th>
                    )}
                    {visibleColumns.percentage && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("percentage")}
                      >
                        Percentage {getSortIcon("percentage")}
                      </th>
                    )}
                    {visibleColumns.examType && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("exam_type")}
                      >
                        Exam Type {getSortIcon("exam_type")}
                      </th>
                    )}
                    {visibleColumns.examNo && (
                      <th
                        className="view-exam-records-th"
                        onClick={() => handleSort("exam_no")}
                      >
                        Exam No {getSortIcon("exam_no")}
                      </th>
                    )}
                    {visibleColumns.totalMarks && (
                      <th className="view-exam-records-th">Total Marks</th>
                    )}
                  </tr>
                </thead>

                <tbody className="view-exam-records-table-body">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td
                        colSpan={getVisibleColumnCount()}
                        className="view-exam-records-empty-cell"
                      >
                        <div className="view-exam-records-empty-state">
                          <FaExclamationTriangle className="view-exam-records-empty-icon" />
                          <div>
                            <p>
                              {students.length === 0
                                ? "No student records found for this exam."
                                : "No students match your search."}
                            </p>
                            <small>
                              {students.length === 0
                                ? "Please check if the exam ID is correct."
                                : "Try a different search term"}
                            </small>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student, index) => {
                      const performanceColor = getPerformanceColor(
                        student.percentage
                      );
                      const performanceLabel = getPerformanceLabel(
                        student.percentage
                      );

                      return (
                        <tr
                          key={student.id}
                          className={`view-exam-records-table-row ${
                            currentFocusIndex === index
                              ? "view-exam-records-row-focused"
                              : ""
                          } ${student.status}`}
                          onClick={() => {
                            if (inputRefs.current[index]) {
                              inputRefs.current[index].focus();
                              inputRefs.current[index].select();
                            }
                          }}
                        >
                          {visibleColumns.studentId && (
                            <td
                              className="view-exam-records-td view-exam-records-td-sticky view-exam-records-td-left"
                              style={{ left: "0px", zIndex: 50 }}
                            >
                              <div className="view-exam-records-student-id">
                                <FaIdCard className="view-exam-records-id-icon" />
                                {student.student_id}
                              </div>
                            </td>
                          )}
                          {visibleColumns.studentName && (
                            <td
                              className="view-exam-records-td view-exam-records-td-sticky view-exam-records-td-left"
                              style={{
                                left: visibleColumns.studentId
                                  ? "120px"
                                  : "0px",
                                zIndex: 49,
                              }}
                            >
                              <div className="view-exam-records-student-name">
                                {student.student_name}
                              </div>
                            </td>
                          )}
                          {visibleColumns.class && (
                            <td className="view-exam-records-td">
                              {student.class}
                            </td>
                          )}
                          {visibleColumns.classSection && (
                            <td className="view-exam-records-td">
                              {student.class_section}
                            </td>
                          )}
                          {visibleColumns.subject && (
                            <td className="view-exam-records-td">
                              {student.subject}
                            </td>
                          )}
                          {visibleColumns.obtainedMarks && (
                            <td className="view-exam-records-td">
                              <div className="view-exam-records-input-container">
                                <input
                                  ref={(el) => (inputRefs.current[index] = el)}
                                  type="number"
                                  className="view-exam-records-marks-input"
                                  value={student.obtained_marks || ""}
                                  onChange={(e) =>
                                    handleMarksUpdate(e, student.id, index)
                                  }
                                  min="0"
                                  max={student.total_marks}
                                  step="0.5"
                                  placeholder="Enter marks"
                                  onFocus={() => setCurrentFocusIndex(index)}
                                  onBlur={() => setCurrentFocusIndex(-1)}
                                  onKeyDown={(e) => {
                                    if (
                                      [
                                        "ArrowUp",
                                        "ArrowDown",
                                        "Enter",
                                      ].includes(e.key)
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                                <div className="view-exam-records-input-hint">
                                  Max: {student.total_marks}
                                </div>
                                <div className="view-exam-records-keyboard-hint">
                                  ↑↓ Enter
                                </div>
                              </div>
                            </td>
                          )}
                          {visibleColumns.percentage && (
                            <td className="view-exam-records-td">
                              <span
                                className={`view-exam-records-percentage ${
                                  parseFloat(student.percentage) >= 50
                                    ? "view-exam-records-percentage-good"
                                    : "view-exam-records-percentage-low"
                                }`}
                                style={{ color: performanceColor }}
                              >
                                {student.percentage}%
                              </span>
                            </td>
                          )}
                          {visibleColumns.examType && (
                            <td className="view-exam-records-td">
                              <span className="view-exam-records-exam-type-badge">
                                {student.exam_type}
                              </span>
                            </td>
                          )}
                          {visibleColumns.examNo && (
                            <td className="view-exam-records-td">
                              {student.exam_no || "N/A"}
                            </td>
                          )}
                          {visibleColumns.totalMarks && (
                            <td className="view-exam-records-td">
                              <div className="view-exam-records-total-marks">
                                {student.total_marks}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Summary */}
            {students.length > 0 && (
              <div className="view-exam-records-footer-summary">
                <div className="view-exam-records-summary-content">
                  <div className="view-exam-records-progress-summary">
                    <div className="view-exam-records-progress-bar">
                      <div
                        className="view-exam-records-progress-fill"
                        style={{ width: `${stats.completionRate}%` }}
                      ></div>
                    </div>
                    <div className="view-exam-records-progress-text">
                      {stats.completionRate}% Complete (
                      {stats.submittedStudents}/{stats.totalStudents})
                    </div>
                    <div className="view-exam-records-keyboard-tips">
                      <span className="view-exam-records-tip">
                        <FaArrowUp /> Arrow Up = Previous
                      </span>
                      <span className="view-exam-records-tip">
                        <FaArrowDown /> Arrow Down = Next
                      </span>
                      <span className="view-exam-records-tip">
                        Enter = Next Row
                      </span>
                    </div>
                    <div className="view-exam-records-exam-id-display">
                      Exam ID: <strong>{examId}</strong>
                      {hasChanges && (
                        <span className="view-exam-records-changes-indicator">
                          <FaExclamationCircle /> Unsaved Changes
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="view-exam-records-summary-actions">
                    <button
                      className="view-exam-records-action-btn view-exam-records-focus-btn"
                      onClick={() => {
                        if (inputRefs.current[0]) {
                          inputRefs.current[0].focus();
                          inputRefs.current[0].select();
                        }
                      }}
                    >
                      <FaArrowDown /> Focus First Input
                    </button>
                    <button
                      className="view-exam-records-action-btn view-exam-records-update-btn"
                      onClick={handleUpdateRecords}
                      disabled={isUpdating || !hasChanges}
                    >
                      {isUpdating ? (
                        <>
                          <FaSpinner className="view-exam-records-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <FaSave /> Update Records
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Scroll Position Indicator */}
          {tableScroll.x > 0 && (
            <div className="view-exam-records-scroll-indicator">
              <FaArrowDown className="view-exam-records-scroll-icon" />
              <span>Scroll → to see more columns</span>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ViewExamRecords;
